import {
	getUserId,
	Identify,
	identify,
	init,
	reset,
	setGroup,
	setUserId,
	track,
	Types,
} from '@amplitude/analytics-browser';

const API_KEY = process.env.REACT_APP_AMPLITUDE_KEY ?? '';

/** Opt-in escape hatch so localhost / stage can be verified without a production deploy. */
const ENABLED_IN_DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

/**
 * Only the production cabinet reports. online-dev, online-subscriptions and
 * stage.wog.ua/<branch> are production builds too, so NODE_ENV cannot tell
 * them apart — the hostname can. Mirrors the deploy targets in .gitlab-ci.yml.
 */
const PRODUCTION_HOSTS = ['online.wog.ua'];

export type LoginMethod = 'otp' | 'password';

/**
 * One login attempt. `otp_sent` is the OTP funnel's denominator, so it fires
 * once per attempt — never on resend. `otp_wrong` / `password_wrong` are the
 * user mistyping (answered with copy and input design); `otp_failed` /
 * `password_failed` are the request itself not landing (answered with
 * infrastructure). Mobile is OTP-only: `login_method` and the password states
 * are web-only.
 */
export type LoginStatus =
	| 'otp_sent'
	| 'otp_wrong'
	| 'otp_failed'
	| 'password_wrong'
	| 'password_failed'
	| 'completed';

/**
 * Onboarding is one funnel, so it is one event with ordered stages. `started`
 * is web-only (the wizard mounted). `step_3` is the user submitting;
 * `submitted` is the backend accepting — the gap between them is where
 * rejected applications show up.
 */
export type OnboardingStage = 'started' | 'step_1' | 'step_2' | 'step_3' | 'submitted';

/**
 * `card` charges a saved bank card. `bank_transfer` (IBAN + QR) and
 * `wallet_transfer` are web-only; `invoice` and `edilo` close the funnel on
 * invoice creation. Google / Apple Pay values arrive with the web pay ticket.
 */
export type DepositMethod = 'card' | 'bank_transfer' | 'wallet_transfer' | 'invoice' | 'edilo';

/** Lets any chart be grouped or filtered by area of the app. */
export type AnalyticsDomain = 'auth' | 'payments' | 'client';

type NoProps = Record<string, never>;

/**
 * The entire tracking surface. Adding a key here is the only way to add an
 * event, and each property shape is enforced at the call site. Names and
 * values match fw-mobile's `src/utils/analytics.ts`: the two apps report to
 * separate projects, and identical names keep their charts comparable and
 * this module portable between the repos.
 */
export type AnalyticsEvents = {
	login: { login_status: LoginStatus; login_method: LoginMethod };
	demo_login: NoProps;
	client_switch: NoProps;
	onboarding_step_completed: { onboarding_step: OnboardingStage };
	deposit: { deposit_method: DepositMethod; deposit_status: 'selected' | 'completed' };
	/** Issuing a new invoice, plain or straight onto eDilo credit. */
	invoice_created: { type: 'standard' | 'edilo_credit' };
	/**
	 * Converting an invoice that already exists onto eDilo credit. `failed`
	 * covers a refusal and a technical failure alike — the backend returns both
	 * as errors, and separating them needs a discriminator it does not send.
	 */
	invoice_financing: { financing_status: 'success' | 'failed' };
	edilo_overview_viewed: NoProps;
};

/**
 * Every event belongs to exactly one area. Declared as a total Record so adding
 * an event to the map above will not compile until its domain is assigned.
 */
const EVENT_DOMAIN: Record<keyof AnalyticsEvents, AnalyticsDomain> = {
	login: 'auth',
	demo_login: 'auth',
	client_switch: 'client',
	onboarding_step_completed: 'auth',
	deposit: 'payments',
	invoice_created: 'payments',
	invoice_financing: 'payments',
	edilo_overview_viewed: 'payments',
};

let started = false;

/** Last identity actually sent, so repeat calls with the same values are free. */
let lastIdentity: string | null = null;

const isReportingHost = (): boolean =>
	process.env.NODE_ENV === 'production' && PRODUCTION_HOSTS.includes(window.location.hostname);

/**
 * Boots the SDK once per runtime. Safe to call repeatedly.
 *
 * `serverZone: 'EU'` only routes ingest to the EU endpoint; the project itself
 * ('WOG Fuel Wallets', next to mobile's 'WOG Business' in the same EU
 * organisation) lives in the EU data centre. Both halves are required and
 * neither can change after data starts flowing.
 *
 * Autocapture stays off in full: page views alone would add ~1.1M events a
 * month against the organisation's 2M free-tier budget shared with mobile,
 * and GA4 already covers page analytics.
 */
export function startAnalytics(): void {
	if (started) return;
	if (!ENABLED_IN_DEBUG && !isReportingHost()) return;

	if (!API_KEY) {
		// Loud on purpose: a missing key is otherwise a silent no-op that looks
		// identical to "nobody used the cabinet today".
		console.warn('[analytics] REACT_APP_AMPLITUDE_KEY is not set — analytics disabled');
		return;
	}

	init(API_KEY, {
		serverZone: 'EU',
		autocapture: false,
		// The SDK would otherwise fetch the project's Autocapture settings on every
		// page load and let them override the local `false` — one toggle in the
		// Amplitude UI could start page views and sessions in production without a
		// deploy. Local config is the only config.
		remoteConfig: { fetchRemoteConfig: false },
		// Debug runs echo every event and its properties to the console, so the
		// instrumentation can be checked without waiting on the dashboard. Verbose,
		// not Debug: Debug also dumps the SDK config — localStorage included — on
		// every call.
		...(ENABLED_IN_DEBUG ? { logLevel: Types.LogLevel.Verbose } : {}),
	});

	started = true;
}

export function trackEvent<E extends keyof AnalyticsEvents>(
	event: E,
	props: AnalyticsEvents[E],
): void {
	if (!started) return;
	track(event, { ...props, domain: EVENT_DOMAIN[event] });
}

/**
 * 8-digit ЄДРПОУ codes belong to legal entities; 10-digit codes are the tax
 * number of a person or sole proprietor, whose "company name" is their own
 * name — personal data that stays out of the analytics vendor. Same rule the
 * onboarding wizard applies to the identifier.
 */
export function legalEntityName(client: {
	codeEgrpou: string;
	counterpartyName: string;
	nameFull: string;
}): string | undefined {
	return /^\d{8}$/.test(client.codeEgrpou) ? client.counterpartyName || client.nameFull : undefined;
}

/**
 * Company identity, not personal identity — the counterparty is what mobile
 * identifies by, and both apps keep one scheme so their numbers compare. The
 * readable name rides along as a user property for legal entities only (see
 * `legalEntityName`), because a dashboard full of raw ids is a dashboard nobody
 * opens.
 */
export function identifyClient(counterparty: string, clientName?: string): void {
	if (!started) return;

	// Deduped here rather than at each caller: the store effect and the client
	// switcher can both report the same identity. `identify` is a network call.
	const next = `${counterparty}|${clientName ?? ''}`;
	if (next === lastIdentity) return;
	lastIdentity = next;

	setUserId(counterparty);
	setGroup('client', counterparty);

	// Unset rather than skip when there is no name, so a value recorded for this
	// counterparty before the rule existed does not linger on the profile.
	const traits = new Identify();
	if (clientName) traits.set('client_name', clientName);
	else traits.unset('client_name');
	identify(traits);
}

/** Anonymises the browser on logout so the next session isn't attributed to the previous client. */
export function clearAnalyticsIdentity(): void {
	if (!started) return;

	// Idempotent: the logout thunk resets first and the store reset re-enters
	// here; a browser that is already anonymous must not mint another device id.
	// `getUserId()` alone is not enough: until `init` settles, the SDK queues
	// `setUserId` and the getter still reports undefined, so an identity set
	// during that window would survive the logout.
	if (lastIdentity === null && getUserId() === undefined) return;
	lastIdentity = null;
	reset();
}
